import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import JobForm from "@/components/JobForm";

export default function JobCreate() {
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);
    const [initial, setInitial] = useState(null);
    const [bootstrapping, setBootstrapping] = useState(false);

    // If the URL has ?seed=<id>, fetch that seed and prefill the form. Lets recruiters
    // start a job *from* a template in one click (matches the "Use this seed" CTA on /seeds).
    useEffect(() => {
        const sid = new URLSearchParams(window.location.search).get("seed");
        if (!sid) return;
        setBootstrapping(true);
        (async () => {
            try {
                // /seeds returns the active+inactive list for this company — find the requested one.
                const { data } = await api.get("/seeds");
                const s = (data?.items || []).find((x) => x.id === sid);
                if (!s) { toast.error("Seed not found"); return; }
                setInitial({
                    description: s.description || "",
                    required_skills: s.required_skills || [],
                    nice_to_have_skills: s.nice_to_have_skills || [],
                    must_haves: s.must_haves || [],
                    red_flags: s.red_flags || [],
                });
                toast.success(`Loaded "${s.name}" — fill in the title to continue`);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally { setBootstrapping(false); }
        })();
    }, []);

    const submit = async (form) => {
        setBusy(true);
        try {
            const { data } = await api.post("/jobs", form);
            toast.success("Job created");
            nav(`/jobs/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-8">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">New job</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Define the role.</h1>
                <p className="text-muted-foreground mt-2">The more specific you are, the smarter the AI screening.</p>
            </div>
            {bootstrapping ? (
                <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground" data-testid="seed-prefill-loading">Loading seed…</div>
            ) : (
                <JobForm mode="create" initial={initial} onSubmit={submit} busy={busy} onCancel={() => nav(-1)} />
            )}
        </div>
    );
}
